import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './AuthModal.module.css';

/**
 * AuthModal
 * A cinematic login-gate modal shown when a logged-out user attempts
 * an action that requires authentication.
 *
 * Props:
 *   isOpen    {boolean}
 *   onClose   {() => void}
 *   message   {string}  – action-specific marketing copy
 */
export default function AuthModal({ isOpen, onClose, message }) {
    const navigate = useNavigate();
    const location = useLocation();
    const from = {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
    };

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // Lock body scroll while open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true" aria-label="Sign up required">
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Decorative legendary shimmer bar */}
                <div className={styles.shimmerBar} />

                <button className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>

                <div className={styles.icon}>🪙</div>

                <h2 className={styles.heading}>Join Aluvo</h2>

                <p className={styles.message}>{message}</p>

                <div className={styles.pillRow}>
                    <span className={styles.pill}>✦ Earn Coins</span>
                    <span className={styles.pill}>💎 Unlock Legendary</span>
                    <span className={styles.pill}>🏛️ Complete Collections</span>
                </div>

                <div className={styles.actions}>
                    <button
                        className={styles.signupBtn}
                        onClick={() => { navigate('/register', { state: { from } }); }}
                    >
                        Create Free Account
                    </button>
                    <button
                        className={styles.loginBtn}
                        onClick={() => { navigate('/login', { state: { from } }); }}
                    >
                        Sign In
                    </button>
                </div>

                <p className={styles.hint}>
                    Already earning coins?{' '}
                    <button className={styles.hintLink} onClick={() => { navigate('/login', { state: { from } }); }}>
                        Sign in →
                    </button>
                </p>
            </div>
        </div>
    );
}
